import React, { useEffect, useState } from 'react'
import st from './Responsible.module.scss'
import { BlockHeader } from 'components/UI/BlockHeader/BlockHeader'
import styles from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import { superAxiosRequest } from 'axiosApi'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import MultiSelectWithCheckBox from '../../UI/components/MultiSelectWithCheckBox/MultiSelectWithCheckBox'

const Responsible = () => {
  const [sources, setSources] = useState([])
  const [sourceEmployees, setSourceEmployees] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchSourceEmployees = () => {
    return superAxiosRequest({
      link: `integration/source_employees`,
      method: 'get',
      crm: true,
    })
      .then((response) => {
        setSourceEmployees(response.data)
        return response.data
      })
      .catch((error) => {
        console.error('Error fetching source employees:', error)
        return []
      })
  }

  const fetchSources = () => {
    return superAxiosRequest({
      link: `integration/sources`,
      method: 'get',
      crm: true,
    })
      .then((response) => {
        return response.data
      })
      .catch((error) => {
        console.error('Error fetching sources:', error)
        return []
      })
  }

  const fetchEmployees = () => {
    return superAxiosRequest({
      method: 'get',
      link: 'integration/employees',
      params: {
        limit: 999,
        short_form: true,
      },
      crm: true,
    })
      .then(({ data }) => {
        const formattedEmployees = data
          ?.map((employee) => ({
            value: employee?.id.toString(),
            label: `${employee?.name}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))

        setEmployees(formattedEmployees)
        return formattedEmployees
      })
      .catch((error) => {
        console.error('Error fetching employees:', error)
        return []
      })
  }

  const mergeSourcesWithEmployees = (sourcesData, sourceEmployeesData) => {
    const merged = sourcesData.map((source) => {
      const sourceEmployeeRelations = sourceEmployeesData.filter(
        (relation) => relation.source.id === source.id,
      )

      const employeesForSource = sourceEmployeeRelations.map((relation) => ({
        id: relation.employee.id,
        name: relation.employee.name,
        full_name: relation.employee.name,
      }))

      return {
        ...source,
        employees: employeesForSource,
      }
    })

    return merged
  }

  useEffect(() => {
    setLoading(true)
    Promise.all([fetchSources(), fetchEmployees(), fetchSourceEmployees()])
      .then(([sourcesData, employeesData, sourceEmployeesData]) => {
        const mergedSources = mergeSourcesWithEmployees(
          sourcesData,
          sourceEmployeesData,
        )
        setSources(mergedSources)
      })
      .catch((error) => {
        console.error('Error fetching data:', error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const updateSourceEmployees = (sourceId, employeeId) => {
    const existingRelation = sourceEmployees.find(
      (relation) =>
        relation.source.id === Number(sourceId) &&
        relation.employee.id === Number(employeeId),
    )

    if (existingRelation) {
      superAxiosRequest({
        method: 'delete',
        link: `integration/source_employees/${existingRelation.id}`,
        crm: true,
      })
        .then(() => {
          // Обновляем локальное состояние
          setSourceEmployees((prev) =>
            prev.filter((relation) => relation.id !== existingRelation.id),
          )

          setSources((prevSources) =>
            prevSources.map((source) =>
              source.id === Number(sourceId)
                ? {
                    ...source,
                    employees: source.employees.filter(
                      (emp) => emp.id !== Number(employeeId),
                    ),
                  }
                : source,
            ),
          )
        })
        .catch((error) => {
          console.error('Error deleting responsible:', error)
        })
    } else {
      superAxiosRequest(
        {
          method: 'post',
          link: `integration/source_employees`,
          crm: true,
        },
        {
          source_id: Number(sourceId),
          employee_id: Number(employeeId),
        },
      )
        .then((response) => {
          const newRelation = response.data
          setSourceEmployees((prev) => [...prev, newRelation])
          setSources((prevSources) =>
            prevSources.map((source) =>
              source.id === Number(sourceId)
                ? {
                    ...source,
                    employees: [
                      ...(source.employees || []),
                      {
                        id: Number(employeeId),
                        name: getEmployeeName(employeeId),
                        full_name: getEmployeeName(employeeId),
                        relationId: newRelation.id,
                      },
                    ],
                  }
                : source,
            ),
          )
        })
        .catch((error) => {
          console.error('Error adding responsible:', error)
        })
    }
  }

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(
      (emp) => emp.value === employeeId.toString(),
    )
    return employee?.label || ''
  }

  return (
    <div className={st.container}>
      <div className={st.settingsChatTemplates}>
        <BlockHeader title='Ответственные'></BlockHeader>
      </div>
      <div className={st.settingsChatContent}>
        <div className={st.searchFilterValues}>
          {loading ? (
            <Loader />
          ) : (
            <table className={`${styles.table} ${styles.tbodyStriped}`}>
              <thead className={`${styles.conversionTbl} ${st.thead}`}>
                <tr>
                  <td className={`${styles.tdTotal} ${styles.tdMiddle}`}>
                    <div className={st.table__checkbox}>Источник</div>
                  </td>
                  <td className={`${styles.tdTotal} ${styles.tdMiddle}`}>
                    <div className={st.table__checkbox}>Ответственные</div>
                  </td>
                </tr>
              </thead>
              <tbody className={styles.conversionTbl}>
                {sources.map((source) => {
                  return (
                    <tr key={source.id}>
                      <td className={`${styles.tdTotal} ${styles.tdMiddle}`}>
                        <div className={st.table__checkbox}>
                          <span>
                            {source.name} {source.comment}
                          </span>
                        </div>
                      </td>
                      <td
                        className={`${styles.tdTotal} ${styles.bdrN} ${styles.tdMiddle} ${styles.bdlN} ${st.responsible}`}
                      >
                        <MultiSelectWithCheckBox
                          options={employees}
                          currentCard={{
                            ...source,
                            employees: source.employees || [],
                          }}
                          onChange={(employeeId) =>
                            updateSourceEmployees(source.id, employeeId)
                          }
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
          {!loading && sources.length === 0 && (
            <div className={st.noData}>No sources found</div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Responsible
